import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./ViewVehicleModal.css";
import defaultTruckImg from "../../assets/logo_vehicle.png";
import { serviceGetOrganizationDetails } from "../../api/serviceFleetMaster";

const formatDate = (dateString) => {
  if (!dateString) {
    return "—";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Safely reads JSON responses.
 * This avoids throwing another error when the server returns plain text
 * or an empty response body.
 */
const readResponseData = async (response) => {
  const responseText = await response.text();

  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return {
      message: responseText,
    };
  }
};

/*
 *  TRIAL     -> access allowed, not cancellable
 *  ACTIVE    -> access allowed, cancellable
 *  PAST_DUE  -> access allowed, cancellable/manageable
 *  INACTIVE  -> no access, can subscribe
 */
const ViewVehicleModal = ({
  vehicle,
  onClose,
  onUpdateVehicleClick,
  handleDeleteVehicleClick,
  onVehicleUpdated,
}) => {
  const organizationCode = sessionStorage.getItem("org_code");
  const [organizationId, setOrganizationId] = useState(null);

  const fetchOrganization = useCallback(async () => {
    try {
      const data = await serviceGetOrganizationDetails();

      setOrganizationId(data._id);
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      toast.error(`Error: ${err?.message ?? "Unknown to load profile"}`);
      setOrganizationId(null);
    }
  }, []);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrganization]);

  const stripeApiUrl = import.meta.env.VITE_STRIPE_ADMIN_URL;

  const deleteTransitionTimer = useRef(null);

  const [deleteStage, setDeleteStage] = useState("idle");

  // null | "subscribe" | "unsubscribe"
  const [subscriptionAction, setSubscriptionAction] = useState(null);

  const [billingInfo, setBillingInfo] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const subscriptionStatus =
    vehicle.subscriptionStatus?.toUpperCase() || "INACTIVE";

  //todo ujj
  const isTrial = subscriptionStatus === "TRIAL";

  const hasPaidSubscription = ["ACTIVE", "PAST_DUE"].includes(
    subscriptionStatus,
  );

  const hasFleetAccess = ["ACTIVE", "TRIAL", "PAST_DUE"].includes(
    subscriptionStatus,
  );

  const subscriptionProcessing = subscriptionAction !== null;

  /*
   * Reset the delete confirmation when a different vehicle is opened.
   * Also clear any pending delete-stage timer.
   */
  useEffect(() => {
    setDeleteStage("idle");

    return () => {
      if (deleteTransitionTimer.current) {
        clearTimeout(deleteTransitionTimer.current);
      }
    };
  }, [vehicle._id]);

  /*
   * Load Stripe subscription billing information for subscribed vehicles.
   *
   * The Stripe subscription belongs to the organisation, so subscribed
   * vehicles in the same organisation normally share this billing date.
   */
  useEffect(() => {
    if (!hasPaidSubscription) {
      setBillingInfo(null);
      setBillingLoading(false);
      return undefined;
    }

    if (!organizationId || !organizationCode || !stripeApiUrl) {
      setBillingInfo(null);
      setBillingLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadBillingInformation = async () => {
      try {
        setBillingLoading(true);

        const query = new URLSearchParams({
          organizationId,
          organizationCode,
        });

        const response = await fetch(
          `${stripeApiUrl}/api/billing/subscription-status?${query.toString()}`,
          {
            method: "GET",
            signal: controller.signal,
          },
        );

        const data = await readResponseData(response);

        if (!response.ok) {
          throw new Error(data.message || "Could not load billing information");
        }

        setBillingInfo(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Billing information error:", error);
          setBillingInfo(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setBillingLoading(false);
        }
      }
    };

    loadBillingInformation();

    return () => {
      controller.abort();
    };
  }, [
    vehicle._id,
    hasPaidSubscription,
    organizationId,
    organizationCode,
    stripeApiUrl,
  ]);

  const validateBillingConfiguration = () => {
    if (!organizationId || !organizationCode) {
      throw new Error("Session expired. Please log in again.");
    }

    if (!stripeApiUrl) {
      throw new Error("Stripe server is not configured.");
    }

    if (!vehicle._id) {
      throw new Error("Invalid vehicle.");
    }
  };

  const initiateDelete = () => {
    if (deleteTransitionTimer.current) {
      clearTimeout(deleteTransitionTimer.current);
    }

    setDeleteStage("transitioning");

    deleteTransitionTimer.current = setTimeout(() => {
      setDeleteStage("confirm");
    }, 300);
  };

  const cancelDelete = () => {
    if (deleteTransitionTimer.current) {
      clearTimeout(deleteTransitionTimer.current);
    }

    setDeleteStage("transitioning");

    deleteTransitionTimer.current = setTimeout(() => {
      setDeleteStage("idle");
    }, 300);
  };

  const handleSubscribe = async () => {
    const confirmed = window.confirm(
      `Subscribe vehicle ${vehicle.vehicleCode} for $30 per month?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setSubscriptionAction("subscribe");

      validateBillingConfiguration();

      const response = await fetch(
        `${stripeApiUrl}/api/billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationId,
            organizationCode,

            // Keep an array for future bulk-subscription support.
            truckIds: [vehicle._id],
          }),
        },
      );

      const data = await readResponseData(response);

      if (!response.ok) {
        throw new Error(data.message || "Could not subscribe vehicle");
      }

      /*
       * A new organisation subscription requires Stripe Checkout.
       */
      if (data.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }

      /*
       * When the organisation already has an active subscription,
       * the server updates its Stripe quantity directly.
       */
      toast.success(data.message || "Vehicle subscription activated");

      await onVehicleUpdated?.();
      onClose();
    } catch (error) {
      console.error("Subscribe vehicle error:", error);

      toast.error(error.message || "Could not subscribe vehicle");
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleUnsubscribe = async () => {
    const confirmed = window.confirm(
      `Cancel the subscription for vehicle ${vehicle.vehicleCode}?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setSubscriptionAction("unsubscribe");

      validateBillingConfiguration();

      const response = await fetch(
        `${stripeApiUrl}/api/billing/remove-trucks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationId,
            organizationCode,

            // Keep an array for future bulk-cancellation support.
            truckIds: [vehicle._id],
          }),
        },
      );

      const data = await readResponseData(response);

      if (!response.ok) {
        throw new Error(
          data.message || "Could not cancel vehicle subscription",
        );
      }

      toast.success(data.message || "Vehicle subscription cancelled");

      await onVehicleUpdated?.();
      onClose();
    } catch (error) {
      console.error("Cancel vehicle subscription error:", error);

      toast.error(error.message || "Could not cancel vehicle subscription");
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleSubscription = () => {
    if (subscriptionProcessing) {
      return;
    }

    if (hasPaidSubscription) {
      handleUnsubscribe();
      return;
    }

    handleSubscribe();
  };

  const getBillingText = () => {
    if (isTrial) {
      return vehicle.trialEndDate
        ? `Trial ends: ${formatDate(vehicle.trialEndDate)}`
        : "30-day trial";
    }

    if (!hasPaidSubscription) {
      return null;
    }

    if (billingLoading) {
      return "Loading billing...";
    }

    if (billingInfo?.cancelAtPeriodEnd && billingInfo?.periodEndDate) {
      return `Ends: ${formatDate(billingInfo.periodEndDate)}`;
    }

    if (billingInfo?.nextPaymentDate) {
      const stripeStatus = billingInfo.status?.toLowerCase();

      const label =
        stripeStatus === "trialing" ? "First billing" : "Next billing";

      return `${label}: ${formatDate(billingInfo.nextPaymentDate)}`;
    }

    return "Next billing: —";
  };

  const billingText = getBillingText();

  const subscriptionButtonText =
    subscriptionAction === "subscribe"
      ? "Processing..."
      : subscriptionAction === "unsubscribe"
        ? "Cancelling..."
        : hasPaidSubscription
          ? "Cancel Subscription"
          : isTrial
            ? "Subscribe Now"
            : "Buy Subscription";

  return (
    <div className="vvm-shell">
      <button
        type="button"
        className="vvm-close-btn"
        onClick={onClose}
        disabled={subscriptionProcessing}
        aria-label="Close vehicle details"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="vvm-card">
        <div className="vvm-top-row">
          <div className="vvm-header">
            <div className="vvm-image-wrap">
              <img
                src={vehicle.imageUrl || defaultTruckImg}
                alt={vehicle.vehicleCode || "Vehicle"}
              />
            </div>

            <div className="vvm-header-info">
              <span className="vvm-id">{vehicle.vehicleCode}</span>

              <div className="vvm-meta">
                <p>
                  <span>Created</span>

                  <span className="vvm-meta-value">
                    {formatDate(vehicle.createdAt)}
                  </span>
                </p>

                <p>
                  <span>Updated</span>

                  <span className="vvm-meta-value">
                    {formatDate(vehicle.updatedAt)}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="vvm-top-divider" />

          <div className="vvm-stripe-panel">
            <span className="vvm-stripe-panel-title">
              <i className="bi bi-credit-card" />
              Billing ex GST
            </span>

            <div className="vvm-stripe-plan-row">
              <span className="vvm-stripe-price">$30/month</span>

              {billingText && (
                <span className="vvm-meta-value">{billingText}</span>
              )}
            </div>
            {/* <div className="vvm-stripe-plan-row">
              <div>
                <span className="vvm-stripe-price">$30/month</span>

                <span
                  className={`vvm-subscription-status ${subscriptionStatus.toLowerCase()}`}
                >
                  {subscriptionStatus === "TRIAL"
                    ? "Trial"
                    : subscriptionStatus === "ACTIVE"
                      ? "Active"
                      : subscriptionStatus === "PAST_DUE"
                        ? "Payment overdue"
                        : "Inactive"}
                </span>
              </div>

              {billingText && (
                <span className="vvm-meta-value">{billingText}</span>
              )}
            </div> */}

            <button
              type="button"
              className={`vvm-btn-subscription ${
                hasPaidSubscription ? "unsubscribe" : "subscribe"
              }`}
              onClick={handleSubscription}
              disabled={subscriptionProcessing}
            >
              <span>{subscriptionButtonText}</span>
            </button>
          </div>

          {hasPaidSubscription && vehicle.subscriptionStartDate && (
            <p>
              <span>Sub Start</span>

              <span className="vvm-meta-value">
                {formatDate(vehicle.subscriptionStartDate)}
              </span>
            </p>
          )}
        </div>

        <div className="vvm-data-area">
          <DataSection title="Axle Capacity" icon="bi-truck">
            <Stat
              label="Steer"
              value={vehicle.weightsMax?.steer}
              unit={vehicle.weightsMax?.unit}
            />

            <Stat
              label="Drive"
              value={vehicle.weightsMax?.drive}
              unit={vehicle.weightsMax?.unit}
            />

            <Stat
              label="Trailer A"
              value={vehicle.weightsMax?.trailerA}
              unit={vehicle.weightsMax?.unit}
            />

            <Stat
              label="Trailer B"
              value={vehicle.weightsMax?.trailerB}
              unit={vehicle.weightsMax?.unit}
            />
          </DataSection>

          <DataSection title="Specifications" icon="bi-truck-front">
            <Stat
              label="Fuel"
              value={vehicle.fuel?.value}
              unit={vehicle.fuel?.unit}
            />

            <Stat label="Mileage" value={vehicle.fuel?.mileage} unit="km max" />

            <Stat
              label="Voltage"
              value={vehicle.voltage?.value}
              unit={vehicle.voltage?.unit}
            />
          </DataSection>

          <DataSection title="Scale Service" icon="bi-calendar">
            <Stat label="Serviced On" value={formatDate(vehicle.servicedOn)} />

            <Stat
              label="Next Due"
              value={formatDate(vehicle.nextServiceOn)}
              urgent
            />
          </DataSection>
        </div>
      </div>

      <footer className={`vvm-footer stage-${deleteStage}`}>
        <div className="vvm-footer-inner">
          {deleteStage === "idle" && (
            <div className="vvm-actions animate-in">
              <button
                type="button"
                className="vvm-edit-cta"
                onClick={() => onUpdateVehicleClick(vehicle)}
                disabled={subscriptionProcessing}
              >
                Edit Vehicle
              </button>

              <button
                type="button"
                className="vvm-delete-icon"
                onClick={initiateDelete}
                disabled={subscriptionProcessing}
                aria-label="Delete vehicle"
              >
                <i className="bi bi-trash3" />
              </button>
            </div>
          )}

          {deleteStage === "confirm" && (
            <div className="vvm-confirm animate-in">
              <p>Permanently remove vehicle?</p>

              <div className="vvm-confirm-btns">
                <button
                  type="button"
                  className="vvm-cancel-btn"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="vvm-delete-btn"
                  onClick={() => handleDeleteVehicleClick(vehicle._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

const DataSection = ({ title, icon, children }) => (
  <div className="vvm-section">
    <header>
      <i className={`bi ${icon}`} />
      {title}
    </header>

    <div className="vvm-grid">{children}</div>
  </div>
);

const Stat = ({ label, value, unit, urgent }) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div className={`vvm-stat ${urgent ? "urgent" : ""}`}>
      <label>{label}</label>

      <div className="vvm-val-wrap">
        <span className="vvm-val">{hasValue ? value : "—"}</span>

        {hasValue && unit ? <span className="vvm-unit">{unit}</span> : null}
      </div>
    </div>
  );
};

export default ViewVehicleModal;
